import { NgClass } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { ApiService } from 'services/api.service';
import { UserMinimalListItem } from 'types/user.interface';

const SEARCH_DEBOUNCE_TIME = 500;
const BLUR_TIMEOUT = 150;

@Component({
  selector: 'app-search-user-input',
  standalone: true,
  imports: [FormsModule, NgClass],
  templateUrl: './search-user-input.component.html',
  styleUrl: './search-user-input.component.scss'
})
export class SearchUserInputComponent {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('dropdown') dropdownRef!: ElementRef<HTMLDivElement>;
  @Input() invalid: boolean = false;
  @Input() disabledUsersOptions: string[] = [];
  @Input() structuralUnitId?: string;
  @Output() userSelected: EventEmitter<string> = new EventEmitter<string>();
  private searchSubject: Subject<string> = new Subject<string>();
  value: string = '';
  users: UserMinimalListItem[] = [];
  isActive: boolean = false;
  isLoading: boolean = false;
  focusedIndex: number = -1;

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    const clickedInsideInput = this.searchInput?.nativeElement.contains(target);
    const clickedInsideDropdown = this.dropdownRef?.nativeElement.contains(target);

    if (!clickedInsideInput && !clickedInsideDropdown) {
      this.isActive = false;
    }
  }

  @HostListener('keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    if (!this.isActive || this.users.length === 0) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusedIndex = (this.focusedIndex + 1) % this.users.length;
        this.scrollToFocused();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.focusedIndex = (this.focusedIndex - 1 + this.users.length) % this.users.length;
        this.scrollToFocused();
        break;

      case 'Enter':
        event.preventDefault();
        if (this.focusedIndex >= 0 && this.focusedIndex < this.users.length) {
          this.selectUser(this.users[this.focusedIndex]);
        }

        break;

      case 'Escape':
        this.isActive = false;
        break;

    }
  }
  scrollToFocused(): void {
    const dropdown = this.dropdownRef?.nativeElement;
    if (!dropdown) return;

    const activeItem = dropdown.children[this.focusedIndex] as HTMLElement;
    if (activeItem) {
      activeItem.scrollIntoView({ block: 'nearest' });
    }
  }

  constructor(private apiService: ApiService) {
    this.searchSubject.pipe(debounceTime(SEARCH_DEBOUNCE_TIME), distinctUntilChanged()).subscribe(() => {
      this.getUsers();
    });
  }

  onChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.value = target.value.trim();
    if (this.value.length > 1) {
      this.searchSubject.next(this.value);
    }
  }

  clearSearch(): void {
    this.value = '';
    this.users = [];
  }

  getUsers(): void {
    this.isLoading = true;

    this.apiService.getMinimalUsersList(this.value, 'Active', false, this.structuralUnitId).subscribe((users: UserMinimalListItem[]) => {
      this.users = this.disabledUsersOptions.length
        ? users.filter(u => !this.disabledUsersOptions.includes(u.id))
        : users;
      this.isLoading = false;
    });
  }

  onFocus(): void {
    this.invalid = false;
    this.isActive = true;

    if (this.value.length === 0) {
      this.getUsers();
    }
  }

  onBlur(): void {
    setTimeout(() => {
      this.isActive = false;
    }, BLUR_TIMEOUT);
  }

  getUserDisplayName(user: UserMinimalListItem): string {
    if (user.structuralUnitCodes?.length) {
      return `${user.fullName} (${user.structuralUnitCodes.join(', ')})`;
    }
    return user.fullName;
  }

  selectUser(user: UserMinimalListItem): void {
    this.value = this.getUserDisplayName(user);
    this.isActive = false;
    this.userSelected.emit(user.id);
    this.searchInput.nativeElement.blur();
  }
}
